'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/ui/logo'
import { trpc } from '@/lib/trpc'
import { handleSignOut } from '@/lib/auth-actions'
import { toast } from 'sonner'
import {
  LogOut,
  Settings,
  Shield,
  Music,
  Plus,
  ChevronsUpDown,
  Loader2,
  LayoutDashboard,
  Menu,
  X,
  Search,
  Info,
  LogIn,
  Home,
  MessageCircle,
  Play,
  DollarSign,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { sanitizeHandle } from '@/lib/reserved-handles'
import { useTranslations } from 'next-intl'

interface HomeNavProps {
  user: {
    id: string
    name?: string | null
    email?: string | null
    image?: string | null
    role: string
  } | null
  subscriptionEnabled?: boolean
}

export function HomeNav({ user, subscriptionEnabled }: HomeNavProps) {
  const t = useTranslations('home')
  const tc = useTranslations('common')
  const router = useRouter()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newArtistName, setNewArtistName] = useState('')
  const [newArtistHandle, setNewArtistHandle] = useState('')
  const [handleInputWarning, setHandleInputWarning] = useState<string | null>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  // Fetch artists for all authenticated users (including admins)
  const { data: artists, isLoading: artistsLoading } = trpc.artist.getMyArtists.useQuery(
    undefined,
    { enabled: !!user }
  )

  const createArtist = trpc.artist.create.useMutation({
    onSuccess: (newArtist) => {
      toast.success(`Created ${newArtist.name}!`)
      setCreateDialogOpen(false)
      setNewArtistName('')
      setNewArtistHandle('')
      // Hard navigation to avoid stale context and ensure fresh server-side render
      window.location.href = `/${newArtist.handle}/dashboard/guided-setup`
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const checkHandle = trpc.artist.checkHandle.useQuery(
    { handle: newArtistHandle },
    { enabled: newArtistHandle.length >= 3 }
  )

  const generateHandle = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s._-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[._-]{2,}/g, (match) => match[0])
      .slice(0, 50)
  }

  const handleNameChange = (name: string) => {
    setNewArtistName(name)
    if (!newArtistHandle || newArtistHandle === generateHandle(newArtistName)) {
      setNewArtistHandle(generateHandle(name))
    }
  }

  const handleCreateArtist = () => {
    if (!newArtistName || !newArtistHandle) return
    createArtist.mutate({
      name: newArtistName,
      handle: newArtistHandle,
    })
  }

  // Not authenticated
  if (!user) {
    return (
      <>
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="flex h-14 sm:h-16 items-center justify-between">
              {/* Left side: Hamburger (mobile) + Logo */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="sm:hidden text-zinc-400 hover:text-white"
                  onClick={() => setMobileNavOpen(!mobileNavOpen)}
                  aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                >
                  {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
                <Link href="/" className="flex items-center">
                  <Logo size="sm" className="sm:hidden" />
                  <Logo size="md" className="hidden sm:flex" />
                </Link>
              </div>

              {/* Desktop navigation (hidden on mobile) */}
              <div className="hidden sm:flex items-center gap-4">
                <Link href="/how-it-works">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white">
                    {t('navHowItWorks')}
                  </Button>
                </Link>
                {subscriptionEnabled && (
                  <Link href="/pricing">
                    <Button variant="ghost" className="text-zinc-400 hover:text-white">
                      {t('navPricing')}
                    </Button>
                  </Link>
                )}
                <Link href="/discover">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white">
                    {t('navDiscover')}
                  </Button>
                </Link>
                <Link href="/demo">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white gap-1.5">
                    <Play className="h-3.5 w-3.5" />
                    {t('navTryDemo')}
                  </Button>
                </Link>
                <Link href="/login">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white">
                    {t('navSignIn')}
                  </Button>
                </Link>
                <Link href="/signup">
                  <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700">
                    {t('navGetStarted')}
                  </Button>
                </Link>
              </div>

              {/* Mobile right side: Get Started button */}
              <div className="sm:hidden">
                <Link href="/signup">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  >
                    {t('navGetStarted')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile nav drawer */}
        {mobileNavOpen && (
          <div className="sm:hidden fixed inset-0 z-40 pt-14">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileNavOpen(false)}
            />
            <nav className="relative w-72 h-full bg-zinc-900 border-r border-zinc-800 overflow-y-auto">
              <div className="p-4 space-y-1">
                <Link
                  href="/"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <Home className="h-5 w-5" />
                  {t('navHome')}
                </Link>
                <Link
                  href="/discover"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <Search className="h-5 w-5" />
                  {t('navDiscoverArtists')}
                </Link>
                <Link
                  href="/how-it-works"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <Info className="h-5 w-5" />
                  {t('navHowItWorks')}
                </Link>
                {subscriptionEnabled && (
                  <Link
                    href="/pricing"
                    onClick={() => setMobileNavOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                  >
                    <DollarSign className="h-5 w-5" />
                    {t('navPricing')}
                  </Link>
                )}
                <Link
                  href="/demo"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <Play className="h-5 w-5" />
                  {t('navTryDemo')}
                </Link>
                <Link
                  href="/contact"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <MessageCircle className="h-5 w-5" />
                  {t('navContactUs')}
                </Link>
                <div className="border-t border-zinc-800 my-3" />
                <Link
                  href="/login"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <LogIn className="h-5 w-5" />
                  {t('navSignIn')}
                </Link>
              </div>
              <div className="p-4 border-t border-zinc-800">
                <Link href="/signup" onClick={() => setMobileNavOpen(false)}>
                  <Button
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  >
                    {t('navStartFreeToday')}
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </>
    )
  }

  // Authenticated user
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            {/* Left side: Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="sm:hidden text-zinc-400 hover:text-white"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <Link href="/" className="flex items-center">
                <Logo size="sm" className="sm:hidden" />
                <Logo size="md" className="hidden sm:flex" />
              </Link>
            </div>

            {/* Desktop navigation (hidden on mobile) */}
            <div className="hidden sm:flex items-center gap-3">
              <Link href="/how-it-works">
                <Button variant="ghost" className="text-zinc-400 hover:text-white">
                  {t('navHowItWorks')}
                </Button>
              </Link>

              {subscriptionEnabled && (
                <Link href="/pricing">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white">
                    {t('navPricing')}
                  </Button>
                </Link>
              )}

              <Link href="/discover">
                <Button variant="ghost" className="text-zinc-400 hover:text-white">
                  {t('navDiscover')}
                </Button>
              </Link>

              <Link href="/demo">
                <Button variant="ghost" className="text-zinc-400 hover:text-white gap-1.5">
                  <Play className="h-3.5 w-3.5" />
                  {t('navTryDemo')}
                </Button>
              </Link>

              {isAdmin && (
                <Link href="/admin">
                  <Button variant="ghost" className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Shield className="h-4 w-4" />
                    {t('navAdmin')}
                  </Button>
                </Link>
              )}

              {artistsLoading ? (
                <Button variant="ghost" className="gap-2" disabled>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </Button>
              ) : artists && artists.length > 0 ? (
                <DropdownMenu open={artistDropdownOpen} onOpenChange={setArtistDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 text-zinc-300 hover:text-white">
                      <Music className="h-4 w-4" />
                      {t('navMyArtists')}
                      <ChevronsUpDown className="h-3 w-3 text-zinc-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[240px] bg-zinc-900 border-zinc-700" align="end">
                    <DropdownMenuLabel className="text-zinc-400 text-xs font-normal">
                      {t('navYourArtistAccounts')}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    {artists.map((artist) => (
                      <DropdownMenuItem
                        key={artist.id}
                        onClick={() => {
                          setArtistDropdownOpen(false)
                          router.push(`/${artist.handle}/dashboard`)
                        }}
                        className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={artist.profileImageUrl || undefined} />
                            <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-xs">
                              {artist.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{artist.name}</p>
                            <p className="text-xs text-zinc-500 truncate">@{artist.handle}</p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <DropdownMenuItem
                      onClick={() => {
                        setArtistDropdownOpen(false)
                        setCreateDialogOpen(true)
                      }}
                      className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('navCreateNewArtist')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="ghost"
                  className="gap-2 text-zinc-300 hover:text-white"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  {t('navCreateArtist')}
                </Button>
              )}

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.image || undefined} alt={user.name || ''} />
                      <AvatarFallback className="bg-zinc-700 text-white">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-700" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-zinc-400">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link href="/admin" className="cursor-pointer text-red-400 focus:text-red-300 focus:bg-zinc-800">
                        <Shield className="mr-2 h-4 w-4" />
                        {t('navAdminDashboard')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link href="/select" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      {t('navArtistDashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('navSettings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  <DropdownMenuItem
                    onClick={() => handleSignOut()}
                    className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('navSignOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile right side: User Avatar Dropdown */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.image || undefined} alt={user.name || ''} />
                      <AvatarFallback className="bg-zinc-700 text-white text-sm">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-700" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-zinc-400">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link href="/admin" className="cursor-pointer text-red-400 focus:text-red-300 focus:bg-zinc-800">
                        <Shield className="mr-2 h-4 w-4" />
                        {t('navAdminDashboard')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {artists && artists.length > 0 ? (
                    <>
                      <DropdownMenuSeparator className="bg-zinc-700" />
                      <DropdownMenuLabel className="text-zinc-500 text-xs font-normal">
                        {t('navYourArtists')}
                      </DropdownMenuLabel>
                      {artists.slice(0, 3).map((artist) => (
                        <DropdownMenuItem
                          key={artist.id}
                          onClick={() => router.push(`/${artist.handle}/dashboard`)}
                          className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                        >
                          <div className="flex items-center gap-2 w-full">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={artist.profileImageUrl || undefined} />
                              <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-xs">
                                {artist.name.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate text-sm">{artist.name}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      {artists.length > 3 && (
                        <DropdownMenuItem asChild>
                          <Link href="/select" className="cursor-pointer text-zinc-400 focus:text-white focus:bg-zinc-800">
                            {t('navViewAllArtists')}
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => setCreateDialogOpen(true)}
                      className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('navCreateArtist')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  <DropdownMenuItem asChild>
                    <Link href="/select" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      {t('navArtistDashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('navSettings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  <DropdownMenuItem
                    onClick={() => handleSignOut()}
                    className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('navSignOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div className="sm:hidden fixed inset-0 z-40 pt-14">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
          />
          <nav className="relative w-72 h-full bg-zinc-900 border-r border-zinc-800 overflow-y-auto">
            <div className="p-4 space-y-1">
              <Link
                href="/"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              >
                <Home className="h-5 w-5" />
                {t('navHome')}
              </Link>
              <Link
                href="/discover"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              >
                <Search className="h-5 w-5" />
                {t('navDiscoverArtists')}
              </Link>
              <Link
                href="/how-it-works"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              >
                <Info className="h-5 w-5" />
                {t('navHowItWorks')}
              </Link>
              {subscriptionEnabled && (
                <Link
                  href="/pricing"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
                >
                  <DollarSign className="h-5 w-5" />
                  {t('navPricing')}
                </Link>
              )}
              <Link
                href="/demo"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              >
                <Play className="h-5 w-5" />
                {t('navTryDemo')}
              </Link>
              <Link
                href="/contact"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                {t('navContactUs')}
              </Link>
            </div>
            <div className="p-4 border-t border-zinc-800">
              <Link href="/select" onClick={() => setMobileNavOpen(false)}>
                <Button
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                >
                  {t('navGoToDashboard')}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* Create New Artist Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">{t('navCreateArtistDialogTitle')}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t('navCreateArtistDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">{t('navArtistNameLabel')}</Label>
              <Input
                id="name"
                value={newArtistName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={t('navArtistNamePlaceholder')}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="handle" className="text-zinc-300">{t('navHandleLabel')}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">@</span>
                <Input
                  id="handle"
                  value={newArtistHandle}
                  onChange={(e) => {
                    const raw = e.target.value
                    const sanitized = sanitizeHandle(raw)
                    setNewArtistHandle(sanitized)
                    if (raw.toLowerCase() !== sanitized) {
                      setHandleInputWarning(t('navHandleWarning'))
                    } else {
                      setHandleInputWarning(null)
                    }
                  }}
                  placeholder={t('navHandlePlaceholder')}
                  className="bg-zinc-800 border-zinc-700 text-white pl-8"
                />
              </div>
              {handleInputWarning && (
                <p className="text-xs text-amber-500">{handleInputWarning}</p>
              )}
              {!handleInputWarning && newArtistHandle.length >= 3 && (
                <p className={cn(
                  "text-xs",
                  checkHandle.data?.available ? "text-green-500" : "text-red-500"
                )}>
                  {checkHandle.isLoading
                    ? t('navCheckingAvailability')
                    : checkHandle.data?.available
                      ? t('navHandleAvailable')
                      : checkHandle.data?.error || t('navHandleNotAvailable')
                  }
                </p>
              )}
              <p className="text-xs text-zinc-500">
                {t('navPublicUrl', { handle: newArtistHandle || 'your-handle' })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-zinc-700"
            >
              {tc('cancel')}
            </Button>
            <Button
              onClick={handleCreateArtist}
              disabled={!newArtistName || !newArtistHandle || !checkHandle.data?.available || createArtist.isPending}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600"
            >
              {createArtist.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('navCreateArtist')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
